import { buildRowTestId } from '../web/utils/test-id.util'

/**
 * Cross-app `data-testid` registry, keyed by app then by a descriptive element name.
 *
 * One definition, two consumers: the component that stamps the `data-testid`, and the Playwright
 * spec that queries it. Renaming a value here moves the markup and the spec together instead of
 * silently unhooking them. The app is carried by the key (`TEST_IDS.core.*`), not the value —
 * core and admin are separate apps/DOMs, so values never clash across apps and need no prefix.
 * Add new apps (`admin`, ...) and new ids under their app, keeping values kebab-case.
 */
export const TEST_IDS = {
  core: {
    // Auth / login (auth.setup.ts) — the registry is the source of these ids.
    loginEmail: 'login-email',
    loginPassword: 'login-password',
    loginSubmit: 'login-submit',
    // Location picker — `locationCard` / `goToBrandLevel` reuse ids already in the markup;
    // `selectLocation` is new.
    locationCard: 'location-card',
    // Value matches main's file-scope+leaf convention (location-selector.component + select-location)
    // so it stays aligned with the external QA suite; the merge had briefly stacked both.
    selectLocation: 'location-selector-select-location',
    // The header location switcher button (opens the picker with per-location options + Go to brand level).
    locationSwitcher: 'location-switcher',
    goToBrandLevel: 'go-to-brand-level',
    // Brand nav (ids generated by Navigation as `nav-<item.id>`; centralized here for the specs).
    // Control Center's brand-nav item id is `control-hub` (layout.constants.jsx) → `nav-control-hub`.
    navControlHub: 'nav-control-hub',
    navSettings: 'nav-settings',
    navReports: 'nav-reports',
    // User Access is a BOTTOM-rail item. Navigation stamps bottom items `nav-<id>` (id 'access'), and
    // it only renders once the collapsed rail is expanded — the spec expands first, then clicks it.
    navAccess: 'nav-access',
    navExpandToggle: 'nav-expand-toggle',
    // OC-320 Content Suite brand-nav item (Navigation stamps nav-<item.id>; id 'contentSuite').
    navContentSuite: 'nav-contentSuite',
    // Settings landing cards — the settings landing (settings.constants.js) stamps each card's
    // data-testid from these ids, so the hook is stable and independent of the translated title.
    settingsLocationsCard: 'Locations',
    settingsRetailCard: 'Retail',
    settingsAccountCard: 'Account',
    settingsWellnessCard: 'Wellness',
    // OC-320 plan-gated vertical tabs — set as the tab's `testId`; specs query the same id.
    retailGiftCards: 'retail-gift-cards-tab',
    // OC-852 retail selling surfaces: the retail dashboard tab strip stamps `retail-tab-<path leaf>`
    // (the plan-gated Gift card tab is `retail-tab-gift-card`); the new-sale vertical tab rides the
    // shared `vertical-tab-<id>` fallback (tab id 'gift-cards').
    retailDashboardTab: (pathLeaf: string) => `retail-tab-${pathLeaf}`,
    newSaleGiftCardsTab: 'vertical-tab-gift-cards',
    accountPayroll: 'account-payroll-tab',
    accountCommissions: 'account-commissions-tab',
    userAccessLocationGroups: 'user-access-location-groups-tab',
    // OC-320 Retail Fees sub-tab + the Holiday Fee row (a Pro-gated fee INSIDE that basic sub-tab).
    retailFeesTab: 'retail-fees-tab',
    retailHolidayFeeRow: 'retail-holiday-fee-row',
    retailNoShowFeeRow: 'retail-no-show-fee-row',
    retailLateCancelFeeRow: 'retail-late-cancel-fee-row',
    // OC-1181 Credits sub-tab (General settings) — hosts the Referral Credits row, gated on
    // ReferAFriend (Premium) inside the basic Credits surface.
    retailCreditsTab: 'retail-credits-tab',
    // OC-1181 Check-In → Door Access row edit pen — gated on AccessSiteDoorManagement (Access add-on).
    // The row id is index-based (3.x), so this stable pen id lets the gate be driven by a test.
    checkInDoorAccessEdit: 'check-in-door-access-edit',
    // OC-1181 the Check-In vertical tab (Location Settings) — stable hook so the tiering walk can open
    // it to reach the Door Access row (its own id is index-based).
    checkInSettingsTab: 'check-in-settings-tab',
    // INT-625 Door Access drawer hooks: with the Access add-on off, an Inception-only brand still
    // reaches the drawer, and the tiering walk asserts Inception is the only provider it offers.
    doorAccessNewAccessPoint: 'door-access-new-access-point',
    doorDrawerProviderSelect: 'field-provider',
    doorDrawerCancel: 'door-drawer-cancel',
    inceptionTestConnection: 'inception-credentials-section-test-connection',
    // OC-320 Memberships sub-tab — hosts the membership editor whose Credit Allocation section carries
    // the SharedCredits/GuestPasses toggles. (Grouped vertical-tabs only stamp an explicit testId.)
    retailMembershipsTab: 'retail-memberships-tab',
    // OC-1306 the lead-time unit beside "Send email X before" on a communication template. Locked
    // to Days for the membership renewal notice, whose window is a regulatory boundary in days.
    communicationTemplateSendBeforeUnit: 'communication-template-send-before-unit',
    communicationTemplateTargetedMemberships: 'communication-template-targeted-memberships',
    // OC-320 scheduler: settings card + Scheduler rules tab + the "Days to display" row (AdvanceBooking,
    // premium — a single gated row inside the basic scheduler-rules Classes section).
    settingsSchedulerCard: 'Scheduler',
    schedulerRulesTab: 'scheduler-rules-tab',
    schedulerDaysToDisplayRow: 'scheduler-days-to-display-row',
    // OC-320 count-based gates: the "add another location/room" action is locked only when the site
    // already has ≥1 (MultiLocation/MultiRoom, premium) — never the switcher/list itself.
    addLocationButton: 'add-location-button',
    addRoomButton: 'add-room-button',
    // Scopes the Business Locations "manage" pen — its NumberedItem id (1.7) collides with a
    // business-customization item of the same number, so the spec queries the pen within this section.
    businessLocationsSection: 'business-locations-section',
    // Business Locations manager: the "manage" pen (NumberedItem "1.7", scoped by the section above)
    // and the "view location" row that opens a location's detail dialog (where "Add room" lives).
    businessLocationsEditRow: 'edit-1.7',
    businessLocationsListViewLocation: 'business-locations-list-view-location',
    // OC-320 Room Resource Configuration row (SpotBasedBooking, NumberedItem "1.8" in the Business
    // Locations section) and the Book-a-Buddy toggle on the Location Operations tab (BookABuddy).
    roomResourceEditRow: 'edit-1.8',
    enableBookABuddy: 'enable-book-a-buddy',
    // OC-320 FamilyAccounts: Clients nav, a record-addressable client row (the name cell <a>), and the
    // plan-gated Relations accordion on the client-info tab (the shared Accordion stamps its summary
    // `accordion-summary-<id>`; the relations section's id is "relations").
    navClients: 'nav-clients',
    clientRow: (clientId: string) => `client-row-${clientId}`,
    clientRelationsAccordion: 'accordion-summary-relations',
    clientFamilyAccountsAccordion: 'accordion-summary-family-accounts',
    // OC-320 MemberMovement: the shared search field and the cross-site "Copy to this location" action
    // that appears on corporate ("All locations") search rows.
    clientSearch: 'field-search',
    clientCopyToLocation: 'copy-to-location-button',
    // OC-320 template credit toggles (SharedCredits/GuestPasses) in the membership editor — shared
    // field components derive `field-<name>` from the RHF field names.
    membershipSharingCredits: 'field-sharing-credits-enabled',
    membershipGuestPass: 'field-guest-pass-allowance-enabled',
    // Shared DialogLayout footer's primary (save/confirm/Edit-toggle) button — stamped by DialogFooter.
    dialogFooterPrimaryAction: 'dialog-footer-primary-action',
    dialogFooterSecondaryAction: 'dialog-footer-secondary-action',
    dialogHeaderClose: 'dialog-header-close',
    // OC-320 Control Center cards: `controlCenterCard` is the card itself (stamped with its title),
    // `controlCenterCardLock` the lock badge a plan-gated card overlays. Title-addressable, so builders.
    controlCenterCard: (title: string) => title,
    controlCenterCardLock: (title: string) => `${title}-lock`,
    // OC-320 Reports left-nav tabs (`reports-nav-tab-<value>`), e.g. the Analytics/BI tab (value 'bi').
    reportsNavTab: (value: string) => `reports-nav-tab-${value}`,
    // OC-320 premium site-integration rows on the Integrations page (`integration-item-<id>`).
    integrationItem: (id: string) => `integration-item-${id}`,
    // A NumberedItem's edit pen — the shared component stamps `edit-<id>` from the row's numeric id.
    editRow: (numberedItemId: string) => `edit-${numberedItemId}`,

    // ── Schedule: Add-session dialog, calendar, session details, roster, waitlist ──
    // (test suite: `e2e/shared/src/pages/core/schedule.page.ts`)
    scheduleAddSession: 'schedule-add-session-button',
    // Add-session template step: one card per template, record-addressable.
    sessionTemplateCard: (templateId: string) => `session-template-card-${templateId}`,
    sessionTemplateCardCustom: 'session-template-card-custom',
    addSessionContinue: 'add-session-continue-button',
    // Instructor (multi-select) and Location (single-select) fields; both derive
    // `<field>-option-<token>` options via the shared select component.
    fieldCoach: 'field-coach',
    fieldLocationId: 'field-location-id',
    // Custom-session form: derived `field-<name>` ids; the time input's comes from the shared
    // TimePicker, and the custom form's Location is `field-location`, not `field-location-id`.
    fieldName: 'field-name',
    fieldSessionType: 'field-session-type',
    fieldSessionDurationHours: 'field-session-duration-hours',
    fieldDate: 'field-date',
    fieldTime: 'field-time',
    fieldRequireSessionFeedback: 'field-require-session-feedback',
    fieldIsDisplayedOnSchedule: 'field-is-displayed-on-schedule',
    addSessionDateField: 'add-session-date-field',
    addSessionTimeInput: 'add-session-time-input',
    // Calendar toolbar: step the visible range forward one period.
    calendarNavNext: 'calendar-nav-next',
    addSessionSubmit: 'add-session-submit-button',
    // The calendar chip for one session, record-addressable (schedule.component.tsx).
    scheduleEvent: (sessionId: string) => `schedule-event-${sessionId}`,
    // Session details dialog: the trash (cancel), pen (edit) and back-chevron
    // icons live in the dialog TITLE, so the pen is present as soon as it mounts.
    sessionDetailsTrashIcon: 'session-details-dialog-trash-icon',
    sessionDetailsPenIcon: 'session-details-dialog-pen-icon',
    sessionDetailsBackIcon: 'session-details-dialog-chevron-left-icon',
    sessionDetailsCopyIcon: 'session-details-dialog-copy-icon',
    sessionDetailsTabBooked: 'session-details-tab-booked',
    sessionDetailsTabWaitlist: 'session-details-tab-waitlist',
    // Edit-session form: capacity ("Session size").
    fieldSessionSize: 'field-session-size',
    // Edit-session form: the template picker (OC-1660) and its record-addressable options.
    fieldEditTemplateId: 'field-edit-template-id',
    fieldEditTemplateIdOption: (templateId: string) =>
      `field-edit-template-id-option-${templateId}`,
    // Booked (roster) tab: Add-clients CTA, bulk-actions button, and the three
    // per-row attendance controls (pending-state buttons + their settled chips).
    bookedTabAddClients: 'booked-tab-add-clients-button',
    bookedTabBulkActions: 'booked-tab-bulk-actions-button',
    attendanceBtnAttended: 'attendance-btn-attended',
    attendanceChipAttended: 'attendance-chip-attended',
    attendanceBtnNoShow: 'attendance-btn-no-show',
    attendanceChipNoShow: 'attendance-chip-no-show',
    // Waitlist tab: Add-clients CTA (disabled until the session is full) and a
    // row's own "Add to booked" promote button.
    waitlistTabAddClients: 'waitlist-tab-add-clients',
    waitlistTabAddToBooked: 'waitlist-tab-add-to-booked',
    // The header's Calendar/List/Team/Room switcher (OC-996 regressed List:
    // stuck on infinite loading). Derived from the field name `calendarMode`
    // via the shared Dropdown; its List-View option is record-addressable by
    // the mode's own value.
    fieldCalendarMode: 'field-calendar-mode',
    fieldCalendarModeOptionList: 'field-calendar-mode-option-list',

    // ── Clients: list row actions, profile cancel dialogs, Edit-address dialog ──
    // (test suite: `e2e/shared/src/pages/core/clients.page.ts`)
    // Row testid: the row-scoped "More actions" button for one client, and that
    // menu's Delete / New-sale items, scoped to the same client.
    clientsTableActionsMore: (clientId: string) => `clients-table-actions-more-${clientId}`,
    clientsTableActionsDelete: (clientId: string) =>
      `clients-table-actions-delete-client-${clientId}`,
    clientsTableActionsNewSale: (clientId: string) => `clients-table-actions-new-sale-${clientId}`,
    // Client profile → Schedule tab: the row kebab, and the early/late
    // cancel-booking dialogs' confirm buttons (which dialog opens is the app's
    // own call from the session's cancel policy against its start time).
    bookedSessionsTabMoreVert: (bookingId: string) => `booked-sessions-tab-more-vert-${bookingId}`,
    earlyCancelConfirm: 'early-cancel-confirm',
    lateCancelConfirm: 'late-cancel-confirm',
    // Client profile → Packages tab: the Credits panel's group-header toggle.
    creditsDialogExpandMore: 'credits-dialog-expand-more-icon',
    // Add-client dialog's address block (business-required on this site).
    fieldStreetAddress: 'field-street-address',
    fieldCity: 'field-city',
    fieldStateProvince: 'field-state-province',
    fieldZipCode: 'field-zip-code',
    fieldUnit: 'field-unit',
    fieldStreetNumber: 'field-street-number',
    // The profile's own Edit-address dialog names the same concept `state`, not
    // `stateProvince` — a different form, a different derived id.
    fieldState: 'field-state',
    // The phone widget's own composite input (country Select + number), whose
    // label briefly computes onto the field ABOVE it while it remounts — never
    // address it with `getByLabel`.
    fieldMobilePhoneNumber: 'field-mobile-phone-number',
    // Profile → Identification Numbers section (OC-1227: clearing the
    // barcode field used to fail the whole updateClient save, silently
    // losing every other field in the same submit). `id={section.id}` makes
    // the accordion summary `accordion-summary-identification-numbers`.
    fieldBarcode: 'field-barcode',
    fieldCustomerId: 'field-customer-id',

    // ── Content Suite editor: title, tags, challenge-specific fields ──
    // (test suite: `e2e/shared/src/pages/core/content-suite.page.ts`)
    contentEditorPage: 'content-editor-page',
    publishToolbar: 'publish-toolbar',
    // Base title field — present for every content type EXCEPT Challenge, whose
    // title lives in a custom component.
    fieldTitle: 'field-title',
    // "Included Package Categories" tags widget (Category/Subcategory's `tags`).
    fieldContentTags: 'field-content-tags',
    articleEditor: 'article-editor',
    contentListGridQuickEdit: 'content-list-grid-quick-edit',
    // Challenge-only fields, all under the form's `fields.*` path.
    fieldFieldsStartType: 'field-fields-start-type',
    fieldFieldsStartDate: 'field-fields-start-date',
    fieldFieldsDuration: 'field-fields-duration',
    fieldFieldsGoalIds: 'field-fields-goal-ids',
    fieldFieldsMinGoals: 'field-fields-min-goals',
    fieldFieldsAttendanceGoalList: 'field-fields-attendance-goal-list',
    fieldFieldsAttendanceRecognitionScope: 'field-fields-attendance-recognition-scope',
    fieldIncludedPackages: 'field-included-packages',
    fieldFieldsStartTypeOption: (value: string) => `field-fields-start-type-option-${value}`,
    fieldFieldsAttendanceRecognitionScopeOption: (value: string) =>
      `field-fields-attendance-recognition-scope-option-${value}`,

    // ── Client Alerts (profile → Notes & Alerts) ──
    // (test suite: `e2e/shared/src/pages/core/client-alerts.page.ts`)
    alertsListNewAlert: 'alerts-list-new-alert',
    // The row-action kebab on a custom alert card. NOT record-scoped by the app
    // (INT-183 never swept this screen), so callers must scope it to the card
    // carrying their own title.
    alertCardMoreVert: 'alert-card-more-vert',

    // ── New Sale (retail) ──
    // (test suite: `e2e/shared/src/pages/core/new-sale.page.ts`)
    // Packages tab, id-derived (`vertical-tab-<tabId>`).
    verticalTabPackages: 'vertical-tab-packages',
    cartCheckout: 'cart-checkout',
    // Payment method field, derived from the form path `payment.0.method`.
    fieldPayment0Method: 'field-payment-0-method',
    fieldPayment0MethodOptionCash: 'field-payment-0-method-option-cash',
    verticalTabMemberships: 'vertical-tab-memberships',
    fieldCreditValidUntil: 'field-credit-valid-until',
    fieldCreditValidUntilCalendar: 'field-credit-valid-until-calendar',

    // ── Draft Invoices (retail → draft invoices) ──
    // (test suite: `e2e/shared/src/pages/core/draft-invoices.page.ts`)
    draftInvoicesSearch: 'field-search',
    draftInvoicesTableActionsMore: (invoiceId: string) =>
      buildRowTestId('draft-invoices-table-actions-more', invoiceId),
    draftInvoicesTableActionsViewDetails: 'draft-invoices-table-actions-view-details',
    draftInvoiceDetailsContent: 'draft-invoice-details-content',
    draftInvoiceDetailsPaymentType: 'draft-invoice-details-payment-type',
    draftInvoiceDetailsItem: (invoiceItemId: string) =>
      `draft-invoice-details-item-${invoiceItemId}`,
    draftInvoiceDetailsSchedule: (invoiceItemId: string) =>
      `draft-invoice-details-schedule-${invoiceItemId}`,

    // ── Payment Details (retail → invoice) — refund dialog ──
    // (test suite: `e2e/shared/src/pages/core/payment-detail.page.ts`)
    paymentDetailsActionsRefundIcon: 'payment-details-actions-refund-icon',
    fieldIsFullRefund: 'field-is-full-refund',
    // The refund-reason control has TWO renderings, decided by whether the site
    // has configured its own reasons: a free-text field or a fixed dropdown.
    fieldRefundReason: 'field-refund-reason',
    fieldReason: 'field-reason',
    fieldReasonOptionCustomerRequest: 'field-reason-option-customer-request',

    // ── Client profile header — Block/Unblock/Delete (OC-100) ──
    // (test suite: `e2e/shared/src/pages/core/clients.page.ts`)
    // The kebab menu, and its Block/Unblock item — ONE id serves both actions;
    // which the click opens is decided by the client's current
    // `membershipStatus` (`use-client-block-actions.hook.tsx`).
    clientProfileActionsMore: 'client-profile-actions-more',
    clientProfileActionsBlock: 'client-profile-actions-block',
    // The Block dialog's reason dropdown — `fieldReason` above (block and the
    // refund dialog both derive from a field literally named "reason"). Its
    // "Other" option carries no stable testid (a site-configured reason's
    // token varies per site) and this searchable Dropdown does not expose
    // options with `role="option"`, so it is queried by text. The custom-
    // reason textarea that follows derives `field-custom-reason` but that id
    // never lands on the actual `<textarea>` (verified live, a testid-contract
    // gap) — addressed by its label instead.

    // ── Schedule template form — session feedback questions (OC-132 / MEM-691) ──
    // (test suite: `e2e/shared/src/pages/core/schedule-templates-settings.page.ts`)
    // The `Row` ids are bases: rows derive `<base>-<questionId>` through `buildRowTestId`, and
    // new-card select options derive `<templateFeedbackSelectQuestion>-option-<questionId>` through
    // `buildOptionTestId`.
    templateFeedbackQuestions: 'template-feedback-questions',
    templateFeedbackEmpty: 'template-feedback-empty',
    templateFeedbackAdd: 'template-feedback-questions-add',
    templateFeedbackMoveUp: 'template-feedback-questions-move-up',
    templateFeedbackMoveDown: 'template-feedback-questions-move-down',
    templateFeedbackToggleFromMenu: 'template-feedback-questions-toggle',
    templateFeedbackRemove: 'template-feedback-questions-remove',
    templateFeedbackQuestionRow: 'template-feedback-question',
    templateFeedbackQuestionTitleRow: 'template-feedback-question-title',
    templateFeedbackToggleRow: 'template-feedback-toggle',
    templateFeedbackMenuRow: 'template-feedback-menu',
    templateFeedbackNewCard: 'template-feedback-new-question',
    templateFeedbackSelectQuestion: 'template-feedback-select-question',
    templateFeedbackCustomToggle: 'template-feedback-custom-question',
    templateFeedbackNewSave: 'template-feedback-new-question-save',
    templateFeedbackNewDelete: 'template-feedback-new-question-delete',
    templateFeedbackNewToggle: 'template-feedback-new-question-toggle',
    templateFeedbackNewMenu: 'template-feedback-new-question-menu',
    scheduleTemplatesRowView: 'schedule-templates-table-action-view',
    scheduleTemplatesRowMoreActions: 'schedule-templates-table-action-more-actions',
    scheduleTemplatesDuplicate: 'duplicate-template',
    scheduleTemplatesApplyToFutureDialog: 'apply-to-future-dialog',
    scheduleTemplatesApplyToFutureApply: 'apply-to-future-dialog-apply',
    scheduleTemplatesApplyIncludeEdited: 'apply-include-edited-sessions',
    scheduleTemplatesBookedSessionsWarning: 'apply-to-future-booked-sessions-warning',
    scheduleTemplatesBookedSessionsAggregatorNotice:
      'apply-to-future-booked-sessions-aggregator-notice',
    scheduleTemplatesHiddenChip: 'schedule-templates-hidden-chip',
    scheduleTemplatesBookedSessionsWarningRow: (sessionId: string) =>
      `apply-to-future-booked-session-${sessionId}`,
    sessionInfoSectionExpand: 'session-info-section-angle-down-icon',
    sessionCardTooltip: 'session-card-tooltip',
    sessionPopoverHiddenFromMembers: 'session-popover-hidden-from-members',
    sessionCardTooltipHiddenFromMembers: 'session-card-tooltip-hidden-from-members',
    sessionInfoMemberVisibility: 'session-info-member-visibility',
    listViewHiddenFromMembers: 'list-view-hidden-from-members',
    viewScheduleTemplateDisplayOnSchedule: 'view-schedule-template-display-on-schedule',

    // OC-132 Location operations rows are numbered from the rows that render, so the number
    // shifts with flags; each row's NumberedItem ids are keyed on these stable row keys instead.
    locationOperationsRows: {
      autoLogout: 'auto-logout',
      cancellationReasons: 'cancellation-reasons',
      blockingReasons: 'blocking-reasons',
      refundReasons: 'refund-reasons',
      customProperties: 'custom-properties',
      enableClientsToSignUp: 'enable-clients-to-sign-up',
      leadSources: 'lead-sources',
      policies: 'policies',
      questionnaireForms: 'questionnaire-forms',
      sessionFeedback: 'session-feedback',
      signUpFieldsBusiness: 'sign-up-fields-business',
      signUpFieldsClient: 'sign-up-fields-client',
      suspensionReasons: 'suspension-reasons',
      bookABuddy: 'book-a-buddy',
      preventDuplicatePhoneSignUp: 'prevent-duplicate-phone-sign-up',
      defaultBusinessProfileTab: 'default-business-profile-tab',
      defaultClientTab: 'default-client-tab',
      defaultAllLocationsSearch: 'default-all-locations-search',
    },
    sessionFeedback: {
      count: 'session-feedback-count',
      add: 'session-feedback-add',
      empty: 'session-feedback-empty',
      newQuestion: 'session-feedback-question-new',
      questionRow: 'session-feedback-question',
      dragHandle: 'session-feedback-drag',
      visibilityToggle: 'session-feedback-toggle',
      rowMenu: 'session-feedback-menu',
      menuEdit: 'menu-edit',
      menuToggle: 'menu-toggle',
      menuDelete: 'menu-delete',
      menuMoveUp: 'menu-move-up',
      menuMoveDown: 'menu-move-down',
      questionText: 'session-feedback-question-text',
      answerType: 'session-feedback-answer-type',
      scale: 'session-feedback-scale',
      characterLimit: 'session-feedback-character-limit',
      minLabel: 'session-feedback-min-label',
      maxLabel: 'session-feedback-max-label',
      helperText: 'session-feedback-helper-text',
      requiredToggle: 'session-feedback-required-toggle',
      preview: 'session-feedback-preview',
      previewSlider: 'session-feedback-preview-slider',
      previewRating: 'session-feedback-preview-rating',
      previewText: 'session-feedback-preview-text',
      previewCount: 'session-feedback-preview-count',
      previewHelper: 'session-feedback-preview-helper',
    },
    planYourVisit: {
      jumpToSection: 'location-configuration-jump-to-plan-your-visit',
      postAlertHeader: 'location-configuration-post-alert',
      section: 'plan-your-visit-section',
      upgradeBanner: 'plan-your-visit-upgrade-banner',
      alertsOnlyCallout: 'plan-your-visit-alerts-only-callout',
      postAlertRow: 'location-alerts-post-alert',
      alertGroup: 'location-alerts-group',
      alertRow: 'location-alert-row',
      alertStatus: 'location-alert-status',
      alertEndNow: 'location-alert-end-now',
      alertEdit: 'location-alert-edit',
      alertDelete: 'location-alert-delete',
      alertConfirm: 'location-alert-confirm',
      liveRightNow: 'live-occupancy-right-now',
      forecastStatus: 'busyness-forecast-status',
      viewHeatmap: 'busyness-forecast-view-heatmap',
      heatmapCell: 'busyness-forecast-heatmap-cell',
      staffedSameAsOpening: 'staffed-hours-same-as-opening',
      amenityRow: 'amenity-row',
      amenityDragHandle: 'amenity-drag-handle',
      amenityToggleVisibility: 'amenity-toggle-visibility',
      amenityEdit: 'amenity-edit',
      amenityRemove: 'amenity-remove',
      addAmenities: 'amenities-add',
      amenityPickerOption: 'amenity-picker',
      amenityEditHours: 'amenity-edit-hours',
      blockRow: 'info-block-row',
      blockDragHandle: 'info-block-drag-handle',
      blockToggleVisibility: 'info-block-toggle-visibility',
      blockEdit: 'info-block-edit',
      blockRemove: 'info-block-remove',
      addBlock: 'info-blocks-add',
      iconOption: 'site-info-icon',
      drawerSave: 'plan-your-visit-drawer-save',
      segmentOption: 'plan-your-visit-segment',
      scheduleEditor: {
        mode: 'opening-schedule-mode',
        copyMonday: 'opening-schedule-copy-monday',
        copyFromOpeningHours: 'opening-schedule-copy-from-opening-hours',
        day: 'opening-schedule-day',
        dayToggle: 'opening-schedule-day-toggle',
        addRange: 'opening-schedule-add-range',
        removeRange: 'opening-schedule-remove-range',
        closesNextDay: 'opening-schedule-closes-next-day',
        addSpecialDay: 'opening-schedule-add-special-day',
        specialDay: 'opening-schedule-special-day',
        removeSpecialDay: 'opening-schedule-remove-special-day',
        specialDayMode: 'opening-schedule-special-day-mode',
      },
      alertDrawer: {
        theme: 'location-alert-theme',
        useTemplate: 'location-alert-use-template',
        startMode: 'location-alert-start-mode',
        endMode: 'location-alert-end-mode',
        closedWarning: 'location-alert-closed-warning',
        preview: 'location-alert-preview',
        submit: 'location-alert-submit',
      },
      closureCancel: {
        step: 'closure-cancel-step',
        session: 'closure-cancel-session',
        sessionCheckbox: 'closure-cancel-session-checkbox',
        progress: 'closure-cancel-progress',
        selectAll: 'closure-cancel-select-all',
        selectNone: 'closure-cancel-select-none',
        notificationMethod: 'closure-cancel-notification',
        skip: 'closure-cancel-skip',
        confirm: 'closure-cancel-confirm',
        empty: 'closure-cancel-empty',
        noPermission: 'closure-cancel-no-permission',
        openSchedule: 'closure-cancel-open-schedule',
        result: 'closure-cancel-result',
        done: 'closure-cancel-done',
      },
      scheduleClosure: {
        banner: 'schedule-location-closure-banner',
        manageAlerts: 'schedule-location-closure-manage-alerts',
        closedDay: 'schedule-closed-day',
      },
    },
    policySettings: {
      add: 'policy-settings-add',
      publishNow: 'policy-settings-publish-now',
      schedule: 'policy-settings-schedule',
      revisionList: 'policy-revision-list',
      revisionRow: 'policy-revision-row',
      cancelScheduled: 'policy-settings-cancel-scheduled',
      deletePolicy: 'policy-settings-delete',
      deletePolicyConfirm: 'policy-settings-delete-confirm',
      jurisdictionRules: 'policy-jurisdiction-rules',
      gatingReadonlyHint: 'policy-settings-gating-readonly-hint',
      importSignedForms: 'policy-settings-import-signed-forms',
      row: 'policy-settings-row',
      edit: 'policy-settings-edit',
      retry: 'policy-settings-retry',
      empty: 'policy-settings-empty',
      publishOutcome: 'policy-settings-publish-outcome',
      scopeChip: 'policy-settings-scope-chip',
      jurisdictionRuleAdd: 'policy-jurisdiction-rule-add',
      jurisdictionRuleRow: 'policy-jurisdiction-rule-row',
      jurisdictionRuleSave: 'policy-jurisdiction-rule-save',
      jurisdictionRuleRetire: 'policy-jurisdiction-rule-retire',
      jurisdictionBandAdd: 'policy-jurisdiction-band-add',
      jurisdictionBandRemove: 'policy-jurisdiction-band-remove',
      openSettings: 'policy-settings-open-settings',
      settingsTitle: 'policy-compliance-settings-title',
      settingsSave: 'policy-compliance-settings-save',
      settingsRetry: 'policy-compliance-settings-retry',
      settingsReadonlyHint: 'policy-compliance-settings-readonly-hint',
      settingsCancel: 'policy-compliance-settings-cancel',
      formCancel: 'policy-settings-form-cancel',
      keepPolicy: 'policy-settings-keep-policy',
      keepScheduled: 'policy-settings-keep-scheduled',
      cancelScheduledConfirm: 'policy-settings-cancel-scheduled-confirm',
      detailTitle: 'policy-settings-detail-title',
      revisionSignaturesHelp: 'policy-revision-signatures-help',
      jurisdictionRuleCancel: 'policy-jurisdiction-rule-cancel',
      jurisdictionRuleRetireConfirm: 'policy-jurisdiction-rule-retire-confirm',
      jurisdictionRuleKeep: 'policy-jurisdiction-rule-keep',
      jurisdictionBandMinAge: 'policy-jurisdiction-band-min-age',
      jurisdictionBandMaxAge: 'policy-jurisdiction-band-max-age',
      jurisdictionBandOutcome: 'policy-jurisdiction-band-outcome',
    },
    clientPolicy: {
      statusCard: 'client-policy-status-card',
      verifyAge: 'client-policy-verify-age',
      recordPaper: 'client-policy-record-paper',
      withdrawVoid: 'client-policy-withdraw-void',
      retry: 'client-policy-retry',
      categoryRow: 'client-policy-category-row',
      verifyAgeSave: 'client-policy-verify-age-save',
      paperContinue: 'paper-form-continue',
      paperBack: 'paper-form-back',
      paperSave: 'paper-form-save',
      paperSlot: 'paper-form-slot',
      paperUpload: 'paper-form-upload',
      paperApprover: 'paper-form-approver',
      paperApproverPin: 'paper-form-approver-pin',
      paperRefusal: 'paper-form-refusal',
      withdrawConfirm: 'client-policy-withdraw-confirm',
      ageVerification: 'client-policy-age-verification',
      titleHelp: 'client-policy-title-help',
      ageHelp: 'client-policy-age-help',
      verificationHelp: 'client-policy-verification-help',
      relationsRetry: 'client-policy-relations-retry',
      verifyAgeCancel: 'client-policy-verify-age-cancel',
      withdrawCancel: 'client-policy-withdraw-cancel',
      paperCancel: 'paper-form-cancel',
      paperSlotSelected: 'paper-form-slot-selected',
      paperSlotRevision: 'paper-form-slot-revision',
      paperSlotSigner: 'paper-form-slot-signer',
      paperSlotSignedOn: 'paper-form-slot-signed-on',
      paperCapacityHelp: 'paper-form-capacity-help',
      paperApproversRetry: 'paper-form-approvers-retry',
      paperSetMyPin: 'record-paper-form-dialog-set-my-pin',
      pinSave: 'approval-pin-dialog-save',
      pinReauthField: 'field-password',
      pinCancel: 'approval-pin-dialog-cancel',
      pinRetry: 'approval-pin-dialog-retry',
    },
    checkInPolicy: {
      gateSection: 'checkin-policy-gate-section',
      gateLoading: 'checkin-policy-gate-loading',
      gateError: 'checkin-policy-gate-error',
      gateRetry: 'checkin-policy-gate-retry',
      gateBanner: 'checkin-policy-gate-banner',
      ageBanner: 'checkin-policy-age-banner',
      gateItem: 'checkin-policy-gate-item',
      send: 'checkin-policy-send',
      completeAtDesk: 'checkin-policy-complete-at-desk',
      recordPaper: 'checkin-policy-record-paper',
      verifyAge: 'checkin-policy-verify-age',
      viewProgress: 'checkin-policy-view-progress',
      dimensions: 'readiness-dims',
      dimensionRow: 'readiness-dim',
      sendPanel: 'pending-items-send-panel',
      sendChannel: 'pending-items-channel',
      sendRecipient: 'pending-items-recipient',
      sendSubmit: 'pending-items-send-submit',
      sendCancel: 'pending-items-send-cancel',
      sendRefusal: 'pending-items-send-refusal',
      sendRefusalFallback: 'send-pending-items-panel-use-text',
      verifyFirst: 'pending-items-verify-first',
      verifyFirstVerify: 'pending-items-verify-first-verify',
      sendTextDestination: 'pending-items-send-text-destination',
      sendConsentOnFile: 'pending-items-send-consent-on-file',
      sendConsentError: 'pending-items-send-consent-error',
      verifyFirstDesk: 'pending-items-verify-first-desk',
      qr: 'pending-items-qr',
      progressPanel: 'pending-items-progress-panel',
      progressSend: 'pending-items-progress-send',
      progressRetry: 'pending-items-progress-retry',
      progressNewQr: 'live-progress-panel-show-new-qr',
      progressQrHidden: 'pending-items-progress-qr-hidden',
      progressDeskBlocked: 'pending-items-progress-desk-blocked',
      progressOpenDesk: 'live-progress-panel-open-desk',
      tracker: 'pending-items-tracker',
      trackerStatus: 'pending-items-tracker-status',
      trackerProgress: 'pending-items-tracker-progress',
      trackerCancel: 'pending-items-tracker-cancel',
      trackerCancelConfirm: 'pending-items-tracker-cancel-confirm',
      trackerCancelConfirmYes: 'pending-items-tracker-stop-link',
      trackerCancelConfirmNo: 'pending-items-tracker-keep-link',
      trackerSendAgain: 'pending-items-tracker-send-again',
      trackerRetry: 'pending-items-tracker-retry',
      eligibility: 'assign-equipment-eligibility',
      eligibilityRetry: 'equipment-eligibility-retry',
      startGate: 'start-timer-policy-gate',
      guardianPresence: 'start-timer-guardian-presence',
      waitlineReason: 'waitline-tab-policy-reason',
      waitlineReasonHelp: 'waitline-tab-policy-reason-help',
      checkInButton: 'check-in-button',
    },
    pendingItems: {
      page: 'pending-items-page',
      loading: 'pending-items-loading',
      loadError: 'pending-items-load-error',
      retry: 'pending-items-retry',
      notFound: 'pending-items-not-found',
      offline: 'pending-items-offline',
      stateSuperseded: 'pending-items-state-superseded',
      stateExpired: 'pending-items-state-expired',
      stateCancelled: 'pending-items-state-cancelled',
      stateLocked: 'pending-items-state-locked',
      stateFailed: 'pending-items-state-failed',
      verify: 'pending-items-verify',
      verifySubmit: 'pending-items-verify-submit',
      guardian: 'pending-items-guardian',
      guardianSubmit: 'pending-items-guardian-submit',
      esignConsent: 'pending-items-esign-consent',
      esignDownloadDisclosure: 'pending-items-esign-download-disclosure',
      esignAccessCode: 'pending-items-esign-access-code',
      esignEmail: 'pending-items-esign-email',
      esignAgree: 'pending-items-esign-agree',
      esignSubmit: 'pending-items-esign-submit',
      esignPaper: 'pending-items-esign-paper',
      esignPaperBack: 'pending-items-esign-paper-back',
      list: 'pending-items-list',
      itemAction: 'pending-items-item-action',
      sign: 'pending-items-sign',
      signTypedName: 'pending-items-sign-typed-name',
      signGuardianFor: 'pending-items-sign-guardian-for',
      signatureMode: 'pending-items-signature-mode',
      signatureCanvas: 'pending-items-signature-canvas',
      signatureClear: 'pending-items-signature-clear',
      signAccept: 'pending-items-sign-accept',
      submit: 'pending-items-submit',
      back: 'pending-items-back',
      skinType: 'pending-items-skin-type',
      skinTypeSubmit: 'pending-items-skin-type-submit',
      documentUpdated: 'pending-items-document-updated',
      documentUpdatedReview: 'pending-items-document-updated-review',
      complete: 'pending-items-complete',
      receiptDownload: 'pending-items-receipt-download',
      handBack: 'pending-items-hand-back',
      receipt: 'pending-items-receipt',
      receiptDocument: 'pending-items-receipt-document',
      receiptDocumentDownload: 'pending-items-receipt-document-download',
      deskBanner: 'pending-items-desk-banner',
      deskIdleWarning: 'pending-items-desk-idle-warning',
      deskMoreTime: 'pending-items-desk-more-time',
      deskLocked: 'pending-items-desk-locked',
      deskReentry: 'pending-items-desk-reentry',
      deskUnlock: 'pending-items-desk-unlock',
      deskSignIn: 'pending-items-desk-sign-in',
      deskSessionLock: 'pending-items-desk-session-lock',
      presenceRequired: 'pending-items-presence-required',
      accessRevoked: 'pending-items-access-revoked',
      deskSessionSignOut: 'desk-session-lock-sign-out',
      deskPin: 'pending-items-desk-pin',
      deskPinError: 'pending-items-desk-pin-error',
      deskPinNotice: 'pending-items-desk-pin-notice',
      deskSwitchMethod: 'desk-lock-screen-switch-method',
      deskUnlockLoading: 'pending-items-desk-unlock-loading',
      deskCopies: 'pending-items-desk-copies',
      remoteCopies: 'pending-items-remote-copies',
      groupHelp: 'pending-items-group-help',
      awaitingGuardian: 'pending-items-awaiting-guardian',
    },
    publicSale: {
      page: 'public-sale-page',
      handBack: 'public-sale-hand-back',
      loading: 'public-sale-loading',
      loadError: 'public-sale-load-error',
      retry: 'public-sale-retry',
      steps: 'public-sale-steps',
      expiry: 'public-sale-expiry',
      verify: 'public-sale-verify',
      verifySubmit: 'public-sale-verify-submit',
      verifyError: 'public-sale-verify-error',
      check: 'public-sale-check',
      checkContinue: 'public-sale-check-continue',
      item: (invoiceItemId: string) => `public-sale-item-${invoiceItemId}`,
      summary: 'public-sale-summary',
      dueToday: 'public-sale-due-today',
      blocked: 'public-sale-blocked',
      sign: 'public-sale-sign',
      signTypedName: 'field-typed-name',
      signCanvas: 'public-sale-sign-canvas',
      signConsent: 'public-sale-sign-consent',
      signSubmit: 'public-sale-sign-submit',
      signBack: 'public-sale-sign-back',
      signError: 'public-sale-sign-error',
      signOnlyDone: 'public-sale-sign-only-done',
      pay: 'public-sale-pay',
      paySubmit: 'public-sale-pay-submit',
      payBack: 'public-sale-pay-back',
      payError: 'public-sale-pay-error',
      payDeclined: 'public-sale-pay-declined',
      payConfirming: 'public-sale-pay-confirming',
      payInProgress: 'public-sale-pay-in-progress',
      nothingLeft: 'public-sale-nothing-left',
      card: 'public-sale-card',
      saveCard: 'public-sale-save-card',
      savedCardLocked: 'public-sale-saved-card-locked',
      terms: 'public-sale-terms',
      termsToggle: 'public-sale-terms-toggle',
      termsShow: 'public-sale-terms-show',
      promo: 'public-sale-promo',
      promoInput: 'field-promo-code',
      promoApply: 'public-sale-promo-apply',
      promoRemove: 'public-sale-promo-remove',
      promoApplied: 'public-sale-promo-applied',
      giftCard: 'public-sale-gift-card',
      giftCardInput: 'field-gift-card-code',
      giftCardRedeem: 'public-sale-gift-card-redeem',
      giftCardRedeemed: 'public-sale-gift-card-redeemed',
      credit: 'public-sale-credit',
      creditSlider: 'public-sale-credit-slider',
      creditApply: 'public-sale-credit-apply',
      creditRemove: 'public-sale-credit-remove',
      creditApplied: 'public-sale-credit-applied',
      optionOpen: (option: string) => `public-sale-${option}-open`,
      optionError: (option: string) => `public-sale-${option}-error`,
      optionCancel: (option: string) => `public-sale-${option}-cancel`,
      done: 'public-sale-done',
      terminal: (kind: string) => `public-sale-terminal-${kind.toLowerCase()}`,
    },
    equipmentCompliance: {
      report: 'equipment-compliance-report',
      scopeNote: 'equipment-compliance-scope-note',
      tile: 'equipment-compliance-tile',
      categoryFilter: 'equipment-compliance-category',
      statusFilter: 'equipment-compliance-status',
      row: 'equipment-compliance-row',
      empty: 'equipment-compliance-empty',
      error: 'equipment-compliance-error',
      retry: 'equipment-compliance-retry',
      noPermission: 'equipment-compliance-no-permission',
      redacted: 'equipment-compliance-redacted',
      bulkSend: 'equipment-compliance-bulk-send',
      bulkSendPreview: 'equipment-compliance-bulk-send-preview',
      bulkSendCap: 'equipment-compliance-bulk-send-cap',
      bulkSendStart: 'equipment-compliance-bulk-send-start',
      bulkSendProgress: 'equipment-compliance-bulk-send-progress',
      bulkSendCancel: 'equipment-compliance-bulk-send-cancel',
      bulkSendDismiss: 'equipment-compliance-bulk-send-dismiss',
      exports: 'equipment-compliance-exports',
      exportsRetry: 'equipment-compliance-exports-retry',
      exportRequest: 'equipment-compliance-export-request',
      exportRequestSubmit: 'equipment-compliance-export-request-submit',
      exportRow: 'equipment-compliance-export-row',
      exportDownload: 'equipment-compliance-export-download',
      exportRevoke: 'equipment-compliance-export-revoke',
      exportRevokeConfirm: 'equipment-compliance-export-revoke-confirm',
      exportsNoPermission: 'equipment-compliance-exports-no-permission',
      exportNotListed: 'equipment-compliance-export-not-listed',
      refetchError: 'equipment-compliance-refetch-error',
      refetchRetry: 'equipment-compliance-refetch-retry',
    },
  },
  admin: {
    // Brand nav (Navigation stamps nav-<item.id>; centralized here for the specs).
    navBrands: 'nav-brands',
    // OC-320 Manage Plan vertical tab — set as the tab's explicit testId so it survives tab
    // reordering; the VerticalTabsNumber fallback (vertical-tab-<id>) is position-coupled.
    managePlanTab: 'manage-plan-tab',
    // Plan tier row edit pen. NumberedItem stamps edit-<id>, where id is the visible ordinal;
    // Plan tier is the first row on Manage Plan, shown as "1.1".
    planTierEditRow: 'edit-1.1',
    // Update plan tier modal — Features / Themes tabs (stamped by the modal, queried by the spec).
    planTabFeatures: 'plan-tab-features',
    planTabThemes: 'plan-tab-themes',
    // One accordion per theme key (access, experience, ...) in the modal's Themes tab. Record-
    // addressable, so it is a builder rather than a literal — stamped by PlanThemesList and queried
    // by the spec through the same call, keeping the id single-sourced.
    planThemeRow: (theme: string) => `plan-theme-${theme}`,
    // OC-320 "Location plan overrides" — the second Manage Plan row (NumberedItem "1.2"): its chip and
    // its edit pen. Whether locations may override the brand plan is decided here, brand-wide.
    locationOverridesChip: 'notMobileChipLabel-1.2',
    locationOverridesEditRow: 'edit-1.2',
    // OC-320 navigation into a single location: the brand's "Locations" tab, then (once inside the
    // location) its "Plan" tab. Both are VerticalTabsNumber fallback ids (vertical-tab-<tab id>), so
    // they are position-derived — brand Locations is tab 1, the location Plan tab is tab 8.
    brandLocationsTab: 'vertical-tab-1',
    locationPlanVerticalTab: 'vertical-tab-8',
    // The location screen's "Login as" button — the one element unique to the location surface, so
    // navigation waits on it to know the location screen (not the brand screen) is mounted.
    loginAsLocation: 'login-as-location',
    // The shared Search component derives its input id from its field name ("search"); the locations
    // table uses the default, so the search box is `field-search`.
    locationSearchInput: 'field-search',
    // OC-320 a single location's Plan tab (Brand > Locations > <site> > Plan): NumberedItem "1.1" again,
    // but on the location surface — its tier chip and edit pen. The pen only renders once the brand has
    // granted this location an override.
    locationPlanTierChip: 'notMobileChipLabel-1.1',
    locationPlanTierEditRow: 'edit-1.1',
    // The PlanTierOverride modal's tier Dropdown (name "planTierOverride") and its options. Both are
    // derived by the shared Dropdown via shared/web/utils/test-id.util.ts; mirrored here so the spec
    // and the markup move together. Option values are the plan tiers (basic / premium / pro).
    planTierOverrideField: 'field-plan-tier-override',
    planTierOverrideOption: (tier: string) => `field-plan-tier-override-option-${tier}`,
    // The inherit option's value is the empty string, so its derived option id ends in a trailing
    // dash (the empty token). Named here because a literal is otherwise unreadable in a spec.
    planTierOverrideInheritOption: 'field-plan-tier-override-option-',
    // Shared DialogLayout footer's primary (save/confirm) button — stamped by DialogFooter.
    dialogFooterPrimaryAction: 'dialog-footer-primary-action',
    // Shared DialogHeader's close (X) button.
    dialogHeaderClose: 'dialog-header-close',
    // OC-852 update plan tier modal — the brand tier radio options (name "planTier"), derived by the
    // shared RadioGroup. Option values are the plan tiers (basic / premium / pro).
    planTierOption: (tier: string) => `field-plan-tier-option-${tier}`,
    // OC-852 add-on toggles and cards. `brandAddonToggle` / `siteAddonToggle` are the inline switch on
    // the Manage Plan / Location plan rows; the `-card-` forms are the toggle inside the fuller card
    // modal. Record-addressable by theme slug, so builders — one definition for markup and specs.
    brandAddonToggle: (slug: string) => `brand-addon-${slug}`,
    brandAddonCard: (slug: string) => `brand-addon-card-${slug}`,
    siteAddonToggle: (slug: string) => `site-addon-${slug}`,
    siteAddonCard: (slug: string) => `site-addon-card-${slug}`,
    // OC-852 the per-location checkbox in the "Location plan overrides" dialog — record-addressable
    // by siteId so a spec can grant/revoke a specific location's override.
    overrideSiteCheckbox: (siteId: string) => `override-site-${siteId}`,
    // OC-852 the plan-tier card in the Add New Brand dialog, addressable by tier id (basic/premium/pro).
    addBrandPlanCard: (tier: string) => `add-brand-plan-card-${tier}`,
    // OC-852 Add New Brand dialog fields — auto-derived `field-<name>` ids from the shared field
    // components (HubSpot company search, brand name, URL prefix), centralised for the e2e.
    addBrandHubspotSearch: 'field-search',
    addBrandName: 'field-brand-name',
    addBrandUrlPrefix: 'field-url-prefix',
    // OC-1181 tier-gated brand-settings affordances. The integration rows pass these as the
    // NumberedItem edit/action testid so the tier-gating spec can find them without the
    // aggregator-count-derived ordinal; the switch rows reuse their field name (NumberedItem stamps
    // switchComp.name), and the table create buttons use shared.tableToolbarPrimaryButton.
    entitlementEgymRow: 'admin-entitlement-egym',
    entitlementGrowHqAction: 'admin-entitlement-grow-hq',
    entitlementLocationGrowRow: 'admin-entitlement-location-grow',
    entitlementMemberMovementSwitch: 'MEMBER_MOVEMENT',
    entitlementDoorAccessSwitch: 'enableDoorAccess',
    entitlementRoyaltyCreate: 'admin-entitlement-royalty-create',
    entitlementPayoutCreate: 'admin-entitlement-payout-create',
    sandboxCopyReport: 'sandbox-copy-report',
    sandboxCopyReportToggle: 'sandbox-copy-report-toggle',
    sandboxCopyReportIds: 'sandbox-copy-report-ids',
  },
  // Cross-app ids stamped by shared components (used identically by core and admin), so they live
  // outside either app's section.
  shared: {
    // The shared Table toolbar's create/primary button — OC-320 gates it (disabled + upgrade
    // tooltip) when the brand's tier excludes the surface's feature.
    tableToolbarPrimaryButton: 'table-toolbar-primary-button',
    // The shared PageTabs "Manage" button (e.g. Clients → manage smart groups).
    pageTabsManageButton: 'page-tabs-manage-button',
    // The shared DatePicker stamps each day of the shown month from its base: the field's
    // `field-<name>` id when it has a name, `date-picker` otherwise.
    datePicker: 'date-picker',
    datePickerDay: (dayOfMonth: number, base = 'date-picker') => `${base}-day-${dayOfMonth}`,
    numberedItemEdit: 'edit',
    numberedItemChip: 'notMobileChipLabel',
    numberedItemRestore: 'restore',
    numberedItemNumber: 'row-number',
    numberedItemDescription: 'row-description',
    numberedItemHelp: 'row-help',
  },
} as const
